import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  CalendarClock,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Megaphone,
  Settings2,
  ShieldCheck,
  Wrench,
} from 'lucide-react';
import { Badge } from '@atoms/Badge';
import { Button } from '@atoms/Button';
import { Card } from '@atoms/Card';
import { Input } from '@atoms/Input';
import { Spinner } from '@atoms/Spinner';
import { adminService } from '@services/adminService';
import { getErrorMessage } from '@services/api';
import type { SystemSetting } from '@/types';

/**
 * Settings that are owned by a dedicated screen or the ad-rate card.
 * They are hidden from the generic list to avoid raw key/value editing.
 */
const MANAGED_KEYS = new Set([
  'ad_credits_per_day',
  'ad_credits_per_24h',
  'restaurant_trial_days',
  'restaurant_subscription_enforced',
  'iyzico_subscription_product_ref',
]);

interface SettingMeta {
  label: string;
  description: string;
  type: 'number' | 'boolean' | 'text';
  unit?: string;
}

/** Friendly metadata for known free-form settings. Unknown keys go to "Gelişmiş". */
const SETTING_META: Record<string, SettingMeta> = {
  // Extend here as new configurable settings are introduced.
};

function describeSetting(setting: SystemSetting): SettingMeta {
  return (
    SETTING_META[setting.key] ?? {
      label: setting.key,
      description: 'Teknik yapılandırma anahtarı',
      type: /^(true|false)$/i.test(setting.value) ? 'boolean' : 'text',
    }
  );
}

export function SettingsSection() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [creditsPerDay, setCreditsPerDay] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const { data: rateData, isLoading: rateLoading } = useQuery({
    queryKey: ['admin', 'ad-rate'],
    queryFn: () => adminService.getAdRate(),
  });

  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => adminService.getSettings(),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'ad-rate'] });
  };

  const saveRateMutation = useMutation({
    mutationFn: () => adminService.setAdRate(Number(creditsPerDay)),
    onSuccess: () => {
      setError(null);
      setCreditsPerDay('');
      invalidate();
    },
    onError: (err) => setError(getErrorMessage(err)),
  });

  const saveSettingMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      adminService.updateSetting(key, value),
    onSuccess: () => {
      setError(null);
      setEditingKey(null);
      setEditValue('');
      invalidate();
    },
    onError: (err) => setError(getErrorMessage(err)),
  });

  if (rateLoading || settingsLoading) return <Spinner />;

  const all = settings ?? [];
  const byKey = new Map(all.map((s) => [s.key, s.value]));
  const advancedSettings = all.filter((s) => !MANAGED_KEYS.has(s.key));

  const currentRate = rateData?.creditsPerDay ?? 1;
  const trialDays = byKey.get('restaurant_trial_days') ?? '30';
  const enforced = (byKey.get('restaurant_subscription_enforced') ?? 'true') !== 'false';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Sistem Ayarları</h1>
        <p className="mt-1 text-sm text-slate-500">
          Platform yapılandırması — reklam, üyelik ve gelişmiş ayarlar
        </p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Ad rate */}
        <Card className="space-y-4 p-5">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
              <Megaphone className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-semibold text-slate-900">Reklam Kredi Oranı</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                Toptancı reklam taleplerinde 1 günlük yayının kredi maliyeti
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
            <span className="text-sm text-slate-600">Mevcut oran</span>
            <span className="text-lg font-bold text-slate-900">
              {currentRate} <span className="text-sm font-medium text-slate-500">kredi/gün</span>
            </span>
          </div>
          <div className="flex items-end gap-2">
            <Input
              label="Yeni oran (kredi/gün)"
              type="number"
              min={1}
              value={creditsPerDay}
              placeholder={String(currentRate)}
              onChange={(e) => setCreditsPerDay(e.target.value)}
            />
            <Button
              loading={saveRateMutation.isPending}
              disabled={!creditsPerDay || Number(creditsPerDay) <= 0}
              onClick={() => saveRateMutation.mutate()}
            >
              Kaydet
            </Button>
          </div>
        </Card>

        {/* Subscription summary → managed on the billing page */}
        <Card className="flex flex-col gap-4 p-5">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
              <CreditCard className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-semibold text-slate-900">Restoran Üyelik Ayarları</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                Deneme süresi, planlar ve aboneler Abonelikler sayfasından yönetilir
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-slate-50 px-4 py-3">
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <CalendarClock className="h-3.5 w-3.5" /> Deneme süresi
              </div>
              <p className="mt-1 text-lg font-bold text-slate-900">
                {trialDays} <span className="text-sm font-medium text-slate-500">gün</span>
              </p>
            </div>
            <div className="rounded-xl bg-slate-50 px-4 py-3">
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <ShieldCheck className="h-3.5 w-3.5" /> Üyelik zorunluluğu
              </div>
              <p className="mt-1">
                {enforced ? (
                  <Badge variant="success">Aktif</Badge>
                ) : (
                  <Badge variant="warning">Devre dışı</Badge>
                )}
              </p>
            </div>
          </div>
          <Link to="/admin/billing" className="mt-auto">
            <Button variant="outline" fullWidth>
              Abonelik Yönetimine Git <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </Card>
      </div>

      {/* Advanced raw settings */}
      <Card padding="none" className="overflow-hidden">
        <button
          type="button"
          onClick={() => setAdvancedOpen((open) => !open)}
          className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-slate-50"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
              <Wrench className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-semibold text-slate-900">Gelişmiş Ayarlar</h2>
              <p className="text-sm text-slate-500">
                Teknik yapılandırma anahtarları — dikkatli düzenleyin
              </p>
            </div>
          </div>
          {advancedOpen ? (
            <ChevronUp className="h-5 w-5 text-slate-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-slate-400" />
          )}
        </button>

        {advancedOpen && (
          <div className="divide-y divide-slate-100 border-t border-slate-100">
            {!advancedSettings.length ? (
              <p className="flex items-center gap-2 px-5 py-4 text-sm text-slate-500">
                <Settings2 className="h-4 w-4" /> Ek teknik ayar bulunmuyor
              </p>
            ) : (
              advancedSettings.map((setting) => {
                const meta = describeSetting(setting);
                const isEditing = editingKey === setting.key;

                if (meta.type === 'boolean' && !isEditing) {
                  const isOn = setting.value !== 'false';
                  return (
                    <div
                      key={setting.key}
                      className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900">{meta.label}</p>
                        <p className="text-sm text-slate-500">{meta.description}</p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={isOn}
                        disabled={saveSettingMutation.isPending}
                        onClick={() =>
                          saveSettingMutation.mutate({
                            key: setting.key,
                            value: isOn ? 'false' : 'true',
                          })
                        }
                        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
                          isOn ? 'bg-brand-500' : 'bg-slate-300'
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
                            isOn ? 'left-[22px]' : 'left-0.5'
                          }`}
                        />
                      </button>
                    </div>
                  );
                }

                return (
                  <div
                    key={setting.key}
                    className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900">{meta.label}</p>
                      {!isEditing && (
                        <p className="truncate text-sm text-slate-500">
                          {setting.value}
                          {meta.unit ? ` ${meta.unit}` : ''}
                        </p>
                      )}
                    </div>
                    {isEditing ? (
                      <div className="flex w-full flex-wrap items-end gap-2 sm:w-auto">
                        <Input
                          label="Değer"
                          type={meta.type === 'number' ? 'number' : 'text'}
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="min-w-[200px]"
                        />
                        <Button
                          size="sm"
                          loading={saveSettingMutation.isPending}
                          onClick={() =>
                            saveSettingMutation.mutate({ key: setting.key, value: editValue })
                          }
                        >
                          Kaydet
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingKey(null)}>
                          İptal
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingKey(setting.key);
                          setEditValue(setting.value);
                        }}
                      >
                        Düzenle
                      </Button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
